import { useEffect, useState } from "react";
import { Megaphone } from "lucide-react";
import { fetchAnnouncements } from "@/lib/api";
import { ClickableImage } from "@/components/Lightbox";

const formatDate = (iso) => {
    try {
        return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
    } catch { return ""; }
};

const AnnouncementCard = ({ a }) => (
    <article
        data-testid={`announcement-card-${a.id}`}
        className="group rounded-2xl overflow-hidden border shadow-md hover:shadow-xl hover:-translate-y-1 transition-all"
        style={{ background: "#383838", borderColor: "#900000" }}
    >
        {a.image_url && (
            <ClickableImage
                src={a.image_url}
                alt={a.title}
                className="w-full h-48 object-cover"
            />
        )}
        <div className="p-5">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider" style={{ color: "#D4AF37" }}>
                <Megaphone size={12} /> {formatDate(a.published_at || a.created_at)}
            </div>
            <h3 className="mt-2 font-serif text-xl text-cream-50 leading-snug">{a.title}</h3>
            <div className="gold-divider my-3 opacity-40" />
            <p className="text-sm text-cream-50/90 whitespace-pre-wrap leading-relaxed">{a.body}</p>
        </div>
    </article>
);

const Announcements = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        fetchAnnouncements(50)
            .then((d) => alive && setItems(d))
            .catch(() => { /* silent */ })
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, []);

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14" data-testid="announcements-page">
            <span className="eyebrow">Parish News</span>
            <h1 className="heading-section mt-1">Announcements</h1>
            <p className="mt-3 text-maroon-700/80 max-w-2xl">
                The latest news and updates from Holy Trinity Catholic Church, Kileleshwa.
            </p>
            <div className="gold-divider mt-5 opacity-60" />

            {loading ? (
                <p className="mt-8 text-maroon-700/70 italic">Loading announcements…</p>
            ) : items.length === 0 ? (
                <p className="mt-10 text-maroon-700/70 italic">No announcements yet. Check back soon.</p>
            ) : (
                <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                    {items.map((a) => <AnnouncementCard key={a.id} a={a} />)}
                </div>
            )}
        </div>
    );
};

// Re-usable homepage row of latest 4
export const AnnouncementsHomeRow = () => {
    const [items, setItems] = useState([]);
    useEffect(() => {
        let alive = true;
        fetchAnnouncements(4)
            .then((d) => alive && setItems(d))
            .catch(() => { /* silent */ });
        return () => { alive = false; };
    }, []);

    if (!items || items.length === 0) return null;
    return (
        <section
            data-testid="home-announcements"
            className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-20"
        >
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-6">
                <div>
                    <span className="eyebrow">Parish News</span>
                    <h2 className="heading-section mt-1">Latest Announcements</h2>
                </div>
                <a
                    href="/announcements"
                    className="text-sm font-semibold text-maroon-700 hover:text-maroon-900"
                >
                    View all announcements →
                </a>
            </div>
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
                {items.map((a) => <AnnouncementCard key={a.id} a={a} />)}
            </div>
        </section>
    );
};

export default Announcements;
